import { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiService } from 'api/services/ApiService';
import { AttioQueryKeys, COMPANY_NAME_KEY, DESC, STATUS_KEY, VALUATION_DATE_KEY, VALUATION_FEE_KEY } from 'common/constants/analyst-dashboards';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { AttioSortDirection } from 'dashboard409a/common/constants/analyst-dashboard';
import { EngagementManagerOption } from 'dashboard409a/components/ValuationsTable/ValuationsTableRow/ValuationsTableRow.types';
import { ValuationStatus } from 'dashboard409a/pages/Workboard/common/types';
import { AttioConsultantService } from 'dashboard409a/services/attio/AttioConsultantService';
import { AttioDealService } from 'dashboard409a/services/attio/AttioDealService';
import { AttioTeamService } from 'dashboard409a/services/attio/AttioTeamService';
import { getAttioUserRecordId } from 'dashboard409a/services/attio-common';
import { getAllAttioValuations, loadBulkNotes } from 'dashboard409a/services/attio-dashboard';
import { AttioConsultantCacheData, AttioDealCacheData, AttioEngagementManagerCacheData, SimplifiedAttioValuation } from 'dashboard409a/services/attio.types';
import { Note } from 'dashboard409a/services/models.types';
import { useLocalStorage } from 'services/hooks/useLocalStorage';
import { getObjectValue, Logger } from 'utilities';
import { UseValuationsWithBulkDataProps, UseValuationsWithBulkDataReturn } from './useValuationsWithBulkData.types';

// Constants
const DEFAULT_ORDER_BY = VALUATION_DATE_KEY;
const DEFAULT_ORDER = DESC;
const USER_RECORD_CACHE_TIME = 60 * 60 * 1000; // 60 minutes
const VALUATIONS_SORT_KEY = 'valuationsSort';
// Generate sorted status options from enum values in workflow order
const SORTED_STATUS_OPTIONS = Object.values(ValuationStatus).map((status, index) => ({
  value: status,
  weight: index + 1
}))

const getStatusWeight = (status: string): number => {
  const statusOption = SORTED_STATUS_OPTIONS.find(option => option.value === status);
  return statusOption?.weight || 0;
};

// Local sorting function - moved outside component to avoid recreation
const sortValuationsLocally = (valuations: SimplifiedAttioValuation[], sortBy: string, sortOrder: AttioSortDirection) => {
  const getValue = (record: SimplifiedAttioValuation): string | number => {
    switch (sortBy) {
      case COMPANY_NAME_KEY:
        return record[sortBy] || '';
      case STATUS_KEY:
        return getStatusWeight(record[sortBy] || '');
      case VALUATION_DATE_KEY:
        return record.valuation_date ? new Date(record.valuation_date).getTime() : 0;
      case VALUATION_FEE_KEY:
        return record.valuation_fee ? Number(record.valuation_fee.replaceAll(/[^0-9.-]/g, '')) || 0 : 0;
      default:
        return '';
    }
  };
  localStorage.setItem(VALUATIONS_SORT_KEY, JSON.stringify({ sortBy, sortOrder }));

  const sorted = [...valuations].sort((a, b) => {
    const aValue = getValue(a);
    const bValue = getValue(b);

    if (aValue === bValue) {
      return 0;
    }
    return (aValue < bValue ? -1 : 1) * (sortOrder === 'asc' ? 1 : -1);
  });
  return sorted;
};

export const useValuationsWithBulkData = ({
  filters,
  orderBy = DEFAULT_ORDER_BY,
  order = DEFAULT_ORDER,
}: UseValuationsWithBulkDataProps): UseValuationsWithBulkDataReturn => {
  const queryClient = useQueryClient();
  
  // State for data loading
  const [displayedValuations, setDisplayedValuations] = useState<SimplifiedAttioValuation[]>([]);
  const [isLoadingAdditionalData, setIsLoadingAdditionalData] = useState(false);
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  const [selectedUserEmail, setSelectedUserEmail] = useLocalStorage<string>('selectedUserEmail', '');
  const [totalCount, setTotalCount] = useState(0);
  const [allValuations, setAllValuations] = useState<SimplifiedAttioValuation[]>([]);
  const [loadedBatches, setLoadedBatches] = useState<Set<number>>(new Set());
  const [otherConsultantsCache, setOtherConsultantsCache] = useState<Map<string, AttioConsultantCacheData>>(new Map());
  const [engagementManagersCache, setEngagementManagersCache] = useState<Map<string, AttioEngagementManagerCacheData>>(new Map());
  const [dealCache, setDealCache] = useState<Map<string, AttioDealCacheData>>(new Map());
  const [notesCache, setNotesCache] = useState<Map<string, Note | null>>(new Map());
  const [statusJustChanged, setStatusJustChanged] = useState(false);
  // Get user email from store
  const [{ user }] = useStore() as unknown as UseStoreValues;
  const { email: userEmail = '', is_superuser = false } = getObjectValue(user);

  // Get user record ID
  // For superusers: use selectedUserEmail if provided, otherwise fall back to userEmail
  // For non-superusers: always use userEmail
  // Never use empty string - always fall back to userEmail if selectedUserEmail is empty
  const effectiveUserEmail = is_superuser && selectedUserEmail ? selectedUserEmail : userEmail;
  const { data: userRecordId, isLoading: isLoadingUserRecord } = useQuery({
    queryKey: [AttioQueryKeys.USER_RECORD_ID, effectiveUserEmail],
    queryFn: () => getAttioUserRecordId(effectiveUserEmail),
    staleTime: USER_RECORD_CACHE_TIME,
    retry: false,
    enabled: !!effectiveUserEmail && effectiveUserEmail.trim() !== '', // Only run if userEmail exists and is not empty
  });

  /// Get users for select list
  const { data: staffUsers = [], isLoading: isLoadingStaffUsers } = useQuery({
    queryKey: ['users-list', 'staff'],
    queryFn: () => ApiService.apiUsersStaffList(),
    enabled: is_superuser,
    staleTime: USER_RECORD_CACHE_TIME,
    retry: false,
  });

  // Helper function to load other consultant data using React Query for caching
  const loadOtherConsultantData = useCallback(async (consultantId: string) => {
    // Check React Query cache first
    const cachedData = queryClient.getQueryData<AttioConsultantCacheData>([
      AttioQueryKeys.OTHER_CONSULTANT,
      consultantId,
    ]);
    
    if (cachedData) {
      return { id: consultantId, data: cachedData };
    }
    
    try {
      const consultantService = new AttioConsultantService();
      const consultant = await consultantService.getOtherConsultant(consultantId);
      
      // Cache in React Query
      queryClient.setQueryData([AttioQueryKeys.OTHER_CONSULTANT, consultantId], consultant);
      
      return { id: consultantId, data: consultant };
    } catch {
      const defaultData = { name: '-', imageSrc: '-' };
      queryClient.setQueryData([AttioQueryKeys.OTHER_CONSULTANT, consultantId], defaultData);
      return { id: consultantId, data: defaultData };
    }
  }, [queryClient]);

  // Helper function to load engagement manager data using React Query for caching
  const loadEngagementManagerData = useCallback(async (engagementManagerId: string) => {
    // Check React Query cache first
    const cachedData = queryClient.getQueryData<AttioEngagementManagerCacheData>([
      AttioQueryKeys.ENGAGEMENT_MANAGER,
      engagementManagerId,
    ]);
    
    if (cachedData) {
      return { id: engagementManagerId, data: cachedData };
    }
    
    try {
      const teamService = new AttioTeamService();
      const manager = await teamService.getTeamMember(engagementManagerId);
      
      // Cache in React Query
      queryClient.setQueryData([AttioQueryKeys.ENGAGEMENT_MANAGER, engagementManagerId], manager);
      
      return { id: engagementManagerId, data: manager };
    } catch {
      const defaultData = { name: '-', imageSrc: '-' };
      queryClient.setQueryData([AttioQueryKeys.ENGAGEMENT_MANAGER, engagementManagerId], defaultData);
      return { id: engagementManagerId, data: defaultData };
    }
  }, [queryClient]);

  // Helper function to load deal data for a batch
  const loadDealDataForBatch = useCallback(async (dealId: string) => {
    if (dealCache.has(dealId)) {
      return { id: dealId, data: dealCache.get(dealId)! };
    }
    
    try {
      const dealService = new AttioDealService();
      const dealData = await dealService.getDealData(dealId);
      const cacheData: AttioDealCacheData = {
        value: dealData.dealTotal,
        due: dealData.due,
        stage: dealData.dealStage,
      };
      return { id: dealId, data: cacheData };
    } catch {
      return { id: dealId, data: { value: '0', due: '0', stage: '-' } };
    }
  }, [dealCache]);

  // Helper function to update displayed valuations with batch data
  const updateDisplayedValuationsWithBatchData = useCallback((
    batchNumber: number,
    newDealCache: Map<string, AttioDealCacheData>,
    newOtherConsultantsCache: Map<string, AttioConsultantCacheData>,
    newEngagementManagersCache: Map<string, AttioEngagementManagerCacheData>
  ) => {
    setDisplayedValuations(prev => {
      const updatedRecords = prev.map((record, index) => {

        // Check if this record is in the current batch
        const batchStartIndex = batchNumber * 20;
        const batchEndIndex = batchStartIndex + 20;
        const isInCurrentBatch = index >= batchStartIndex && index < batchEndIndex;

        if (!isInCurrentBatch) {
          return record;
        }

        // For records in the current batch, prioritize newly loaded cache data
        // Fall back to React Query cache if not available in the new cache maps
        const otherConsultant = record.other_consultant_id
          ? newOtherConsultantsCache.get(record.other_consultant_id) ||
            queryClient.getQueryData<AttioConsultantCacheData>([
              AttioQueryKeys.OTHER_CONSULTANT,
              record.other_consultant_id,
            ])
          : newOtherConsultantsCache.get('no-id') || { name: '-', imageSrc: '-' };
        
        const engagementManager = record.engagement_manager_id
          ? newEngagementManagersCache.get(record.engagement_manager_id) ||
            queryClient.getQueryData<AttioEngagementManagerCacheData>([
              AttioQueryKeys.ENGAGEMENT_MANAGER,
              record.engagement_manager_id,
            ])
          : newEngagementManagersCache.get('no-id') || { name: '-', imageSrc: '-' };
        // Get note from React Query cache (notes are loaded separately)
        const note = queryClient.getQueryData<Note | null>([
          AttioQueryKeys.BULK_NOTES,
          record.record_id,
        ]);

        const deal = record.deal_id 
          ? newDealCache.get(record.deal_id) 
          : newDealCache.get('no-id');

        const hasNotesData = note !== undefined;
        const hasOtherConsultantData = record.other_consultant_id 
          ? (otherConsultant !== null && otherConsultant !== undefined) 
          : true;
        const hasEngagementManagerData = record.engagement_manager_id 
          ? (engagementManager !== null && engagementManager !== undefined) 
          : true;
        const hasDealData = record.deal_id 
          ? (deal !== null && deal !== undefined) 
          : true;
        const shouldRemoveLoading = hasNotesData && hasOtherConsultantData && hasEngagementManagerData && hasDealData;

        return {
          ...record,
          note: note || null,
          other_consultant: otherConsultant?.name || '-',
          other_consultant_image_src: otherConsultant?.imageSrc || '-',
          engagement_manager: engagementManager?.name || '-',
          engagement_manager_image_src: engagementManager?.imageSrc || '-',
          deal: deal?.value || '-',
          deal_due: deal?.due || '-',
          deal_stage: deal?.stage || '-',
          is_loading_additional_data: !shouldRemoveLoading,
        };
      });
      
      // Return updated records without re-sorting to preserve current order
      return updatedRecords;
    });
  }, [queryClient]);

  // Load additional data for a batch of 20 records
  const loadAdditionalDataForBatch = useCallback(async (batchNumber: number) => {
    if (loadedBatches.has(batchNumber)) {
      return;
    }

    const startIndex = batchNumber * 20;
    const endIndex = startIndex + 20;
    const batch = displayedValuations.slice(startIndex, endIndex);

    if (batch.length === 0) {
      return;
    }

    setIsLoadingAdditionalData(true);

    try {
      // Get unique IDs
      const otherConsultantIds = batch
        .filter(record => record.other_consultant_id)
        .map(record => record.other_consultant_id!);
      const dealIds = batch
        .filter(record => record.deal_id)
        .map(record => record.deal_id!);
      const engagementManagerIds = batch
        .filter(record => record.engagement_manager_id)
        .map(record => record.engagement_manager_id!);

      // Load data in parallel
      const [otherConsultantResults, engagementManagerResults, dealResults, notesResults] = await Promise.all([
        Promise.all(otherConsultantIds.map(loadOtherConsultantData)),
        Promise.all(engagementManagerIds.map(loadEngagementManagerData)),
        Promise.all(dealIds.map(loadDealDataForBatch)),
        Promise.all(batch.map(record => queryClient.getQueryData<Note>([
          AttioQueryKeys.BULK_NOTES,
          record.record_id,
        ])))
      ]);

      // Update caches
      const newOtherConsultantsCache = new Map(otherConsultantsCache);
      const newEngagementManagersCache = new Map(engagementManagersCache);
      const newDealCache = new Map(dealCache);
      const newNotesCache = new Map(notesCache);
      otherConsultantResults.forEach(({ id, data }) => newOtherConsultantsCache.set(id, data));
      engagementManagerResults.forEach(({ id, data }) => newEngagementManagersCache.set(id, data));
      dealResults.forEach(({ id, data }) => newDealCache.set(id, data));
      notesResults.forEach(result => {
        if (result) {
          if (result.id) {
            newNotesCache.set(result.id.toString(), result);
          } 
        }
      });
      // Add default values for records without IDs
      const hasNoOtherConsultantId = batch.some(record => !record.other_consultant_id);
      const hasNoEngagementManagerId = batch.some(record => !record.engagement_manager_id);
      
      if (hasNoOtherConsultantId && !newOtherConsultantsCache.has('no-id')) {
        newOtherConsultantsCache.set('no-id', { name: '-', imageSrc: '-' });
      }
      if (hasNoEngagementManagerId && !newEngagementManagersCache.has('no-id')) {
        newEngagementManagersCache.set('no-id', { name: '-', imageSrc: '-' });
      }

      setDealCache(newDealCache);
      setOtherConsultantsCache(newOtherConsultantsCache);
      setEngagementManagersCache(newEngagementManagersCache);
      setNotesCache(newNotesCache);
      updateDisplayedValuationsWithBatchData(batchNumber, newDealCache, newOtherConsultantsCache, newEngagementManagersCache);
      setLoadedBatches(prev => new Set([...prev, batchNumber]));

    } catch (error) {
      Logger.error('Error loading additional data for batch', error);
    } finally {
      setIsLoadingAdditionalData(false);
    }
  }, [loadedBatches, displayedValuations, loadOtherConsultantData, loadEngagementManagerData, loadDealDataForBatch, otherConsultantsCache, engagementManagersCache, dealCache, notesCache, updateDisplayedValuationsWithBatchData, queryClient]);

  // Load all valuations (1000 at a time) - sorting applied locally
  const { data: allValuationsData, isLoading: isLoadingAllValuations } = useQuery({
    queryKey: ['valuations-all', userRecordId, filters],
    queryFn: () => getAllAttioValuations({
      userRecordId: userRecordId!,
      filters,
    }),
    enabled: !!userRecordId,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 10 * 60 * 1000, // 10 minutes cache
    gcTime: 30 * 60 * 1000, // 30 minutes garbage collection
  });

  // Load initial data when all valuations are loaded
  useEffect(() => {
    if (allValuationsData) {
      if (allValuationsData.data && allValuationsData.data.length > 0) {
        // Store all valuations and set initial display
        setAllValuations(allValuationsData.data);
        setTotalCount(allValuationsData.totalCount || allValuationsData.data.length);
        
        if (displayedValuations.length === 0) {
          // Show loading state first
          const allRecordsWithLoading = allValuationsData.data.map((record: SimplifiedAttioValuation) => ({
            ...record,
            is_loading_additional_data: true,
            other_consultant: null,
            other_consultant_image_src: null,
            engagement_manager: null,
            engagement_manager_image_src: null,
            notes: null, // Will be loaded
            deal: null,
            deal_due: null,
            deal_stage: null,
          }));
          setDisplayedValuations(allRecordsWithLoading);
        }
      } else {
        // No data case - set empty arrays and mark as complete
        setAllValuations([]);
        setDisplayedValuations([]);
        setTotalCount(0);
        setIsInitialLoadComplete(true);
      }
    }
  }, [allValuationsData, displayedValuations.length]);

  // Apply local sorting when data or sorting parameters change
  useEffect(() => {
    if (allValuations.length > 0) {
      // Determine if we should apply delay: only when sorting by status AND status was just changed
      const isSortingByStatus = orderBy === STATUS_KEY;
      const shouldDelay = isSortingByStatus && statusJustChanged;
      const delay = shouldDelay ? 5000 : 0;
      
      const performSort = () => {
        // Recalculate sorting to ensure we have latest values
        let sortedValuations: SimplifiedAttioValuation[] = [];
        const localSort = localStorage.getItem(VALUATIONS_SORT_KEY);
        
        if (orderBy !== DEFAULT_ORDER_BY || order !== DEFAULT_ORDER) {
          sortedValuations = sortValuationsLocally(allValuations, orderBy, order);
        } else if (localSort) {
          const { sortBy, sortOrder } = JSON.parse(localSort);
          sortedValuations = sortValuationsLocally(allValuations, sortBy, sortOrder);
        } else {
          sortedValuations = sortValuationsLocally(allValuations, orderBy, order);
        }
        
        setDisplayedValuations(prevDisplayedValuations => {
          // Create a map of existing data by record_id for quick lookup
          const existingDataMap = new Map(
            prevDisplayedValuations.map(record => [
              record.record_id, 
              {
                other_consultant: record.other_consultant,
                other_consultant_image_src: record.other_consultant_image_src,
                engagement_manager: record.engagement_manager,
                engagement_manager_image_src: record.engagement_manager_image_src,
                note: record.note,
                deal: record.deal,
                deal_due: record.deal_due,
                deal_stage: record.deal_stage,
                is_loading_additional_data: record.is_loading_additional_data,
              }
            ])
          );
          
          return sortedValuations.map(record => {
            const existingData = existingDataMap.get(record.record_id);
            const cachedDeal = record.deal_id ? dealCache.get(record.deal_id) : undefined;
            
            // Try to get cached data from React Query
            const cachedNote = queryClient.getQueryData<Note | null>([
              AttioQueryKeys.BULK_NOTES,
              record.record_id,
            ]);
            const cachedOtherConsultant = record.other_consultant_id
              ? queryClient.getQueryData<AttioConsultantCacheData>([
                AttioQueryKeys.OTHER_CONSULTANT,
                record.other_consultant_id,
              ])
              : { name: '-', imageSrc: '-' };
            const cachedEngagementManager = record.engagement_manager_id
              ? queryClient.getQueryData<AttioEngagementManagerCacheData>([
                AttioQueryKeys.ENGAGEMENT_MANAGER,
                record.engagement_manager_id,
              ])
              : { name: '-', imageSrc: '-' };
            
            // Use existing data if available, otherwise use cached data, otherwise use defaults
            const otherConsultant = existingData?.other_consultant ?? (cachedOtherConsultant?.name || '-');
            const otherConsultantImageSrc = existingData?.other_consultant_image_src ?? (cachedOtherConsultant?.imageSrc || '-');
            const engagementManager = existingData?.engagement_manager ?? (cachedEngagementManager?.name || '-');
            const engagementManagerImageSrc = existingData?.engagement_manager_image_src ?? (cachedEngagementManager?.imageSrc || '-');
            const note = existingData?.note ?? (cachedNote || null);
            const deal = existingData?.deal ?? cachedDeal?.value ?? record.deal ?? '-';
            const dealDue = existingData?.deal_due ?? cachedDeal?.due ?? record.deal_due ?? '-';
            const dealStage = existingData?.deal_stage ?? cachedDeal?.stage ?? record.deal_stage ?? '-';
            
            // Determine if we should show loading state
            const hasNotesData = note !== undefined;
            const hasOtherConsultantData = record.other_consultant_id 
              ? (cachedOtherConsultant !== null && cachedOtherConsultant !== undefined) 
              : true;
            const hasEngagementManagerData = record.engagement_manager_id 
              ? (cachedEngagementManager !== null && cachedEngagementManager !== undefined) 
              : true;
            const isRecordLoadingAdditionalData = existingData?.is_loading_additional_data ?? !(hasNotesData && hasOtherConsultantData && hasEngagementManagerData);
            
            if (existingData) {
              return {
                ...record,
                other_consultant: otherConsultant,
                other_consultant_image_src: otherConsultantImageSrc,
                engagement_manager: engagementManager,
                engagement_manager_image_src: engagementManagerImageSrc,
                note,
                deal,
                deal_due: dealDue,
                deal_stage: dealStage,
                is_loading_additional_data: isRecordLoadingAdditionalData
              };
            }
            return {
              ...record,
              is_loading_additional_data: isRecordLoadingAdditionalData,
              other_consultant: otherConsultant,
              other_consultant_image_src: otherConsultantImageSrc,
              engagement_manager: engagementManager,
              engagement_manager_image_src: engagementManagerImageSrc,
              note,
              deal,
              deal_due: dealDue,
              deal_stage: dealStage,
            };
          });
        });
        
        // Reset the status change flag after sorting
        if (shouldDelay) {
          setStatusJustChanged(false);
        }
      };
      
      const timeoutId = setTimeout(performSort, delay);
      
      return () => {
        clearTimeout(timeoutId);
      };
    }
    return undefined;
  }, [allValuations, orderBy, order, queryClient, dealCache, statusJustChanged]);

  // Reset status change flag when sorting changes to something other than status
  useEffect(() => {
    if (orderBy !== STATUS_KEY && statusJustChanged) {
      setStatusJustChanged(false);
    }
  }, [orderBy, statusJustChanged]);

  // Helper function to update displayed valuations with notes
  const updateDisplayedValuationsWithNotes = useCallback((notesMap: Map<string, Note | null>) => {
    setDisplayedValuations(prev => {
      const updatedRecords = prev.map(record => {
        const note = notesMap.get(record.record_id) || null;
        return {
          ...record,
          note,
        };
      });
      
      // Return updated records without re-sorting to preserve current order
      return updatedRecords;
    });
  }, []);

  // Helper function to set default notes
  const setDefaultNotes = useCallback(() => {
    setDisplayedValuations(prev => {
      const updatedRecords = prev.map(record => ({
        ...record,
        note: null,
      }));
      
      // Return updated records without re-sorting to preserve current order
      return updatedRecords;
    });
  }, []);

  // Load notes in bulk using React Query for caching
  const { data: bulkNotesData, isLoading: isLoadingBulkNotes } = useQuery<Map<string, Note | null>>({
    queryKey: [AttioQueryKeys.BULK_NOTES, allValuations.map(record => record.record_id).sort((a, b) => a.localeCompare(b)).join(',')],
    queryFn: async () => {
      if (allValuations.length === 0) {
        return new Map<string, Note | null>();
      }
      const valuationIds = allValuations.map(record => record.record_id);
      return loadBulkNotes(valuationIds);
    },
    enabled: allValuations.length > 0,
    retry: false,
    staleTime: 10 * 60 * 1000, // 10 minutes cache
    gcTime: 30 * 60 * 1000, // 30 minutes garbage collection
  });

  // Update displayed valuations with notes from React Query cache
  useEffect(() => {
    if (bulkNotesData) {
      // Ensure bulkNotesData is a Map (React Query may deserialize it as a plain object)
      let notesMap: Map<string, Note | null>;
      if (bulkNotesData instanceof Map) {
        notesMap = bulkNotesData;
      } else if (typeof bulkNotesData === 'object' && bulkNotesData !== null) {
        // Convert plain object to Map if React Query deserialized it
        notesMap = new Map(Object.entries(bulkNotesData as Record<string, Note | null>));
      } else {
        notesMap = new Map<string, Note | null>();
      }

      // Store notes in React Query cache individually for easy lookup
      notesMap.forEach((note, valuationId) => {
        queryClient.setQueryData([AttioQueryKeys.BULK_NOTES, valuationId], note);
      });
      
      updateDisplayedValuationsWithNotes(notesMap);
      setIsInitialLoadComplete(true);
    } else if (allValuations.length > 0 && !isLoadingBulkNotes) {
      // No notes data and not loading - set default
      setDefaultNotes();
      setIsInitialLoadComplete(true);
    }
  }, [bulkNotesData, allValuations.length, isLoadingBulkNotes, updateDisplayedValuationsWithNotes, setDefaultNotes, queryClient]);

  // Load additional data for all records in batches of 20
  useEffect(() => {
    if (displayedValuations.length > 0 && isInitialLoadComplete) {
      // Load additional data for all records in batches of 20
      const totalBatches = Math.ceil(displayedValuations.length / 20);
      for (let batchNumber = 0; batchNumber < totalBatches; batchNumber++) {
        if (!loadedBatches.has(batchNumber)) {
          loadAdditionalDataForBatch(batchNumber);
          break; // Load one batch at a time to avoid overwhelming the API
        }
      }
    }
  }, [displayedValuations.length, isInitialLoadComplete, loadedBatches, loadAdditionalDataForBatch]);

  // Functions to update individual valuations with cache invalidation
  const updateValuation = useCallback((updatedValuation: SimplifiedAttioValuation) => {
    // Update displayed valuations optimistically
    setDisplayedValuations(prevValuations => 
      prevValuations.map(valuation => 
        valuation.record_id === updatedValuation.record_id 
          ? { ...valuation, ...updatedValuation }
          : valuation
      )
    );

    // Invalidate and refetch the main query to ensure consistency
    queryClient.invalidateQueries({
      queryKey: [AttioQueryKeys.VALUATIONS_ALL, userRecordId, filters, orderBy, order]
    });
  }, [queryClient, userRecordId, filters, orderBy, order]);

  const updateValuationStatus = useCallback((recordId: string, newStatus: string) => {
    setDisplayedValuations(prevValuations => 
      prevValuations.map(valuation => 
        valuation.record_id === recordId 
          ? { ...valuation, status: newStatus }
          : valuation
      )
    );

    setAllValuations(prevValuations => 
      prevValuations.map(valuation => 
        valuation.record_id === recordId 
          ? { ...valuation, status: newStatus }
          : valuation
      )
    );
    
    // Mark that status was just changed to trigger delayed sorting if sorting by status
    setStatusJustChanged(true);
  }, []);

  const updateValuationNotes = useCallback((recordId: string, newNotes: string) => {
    // Create updated note object
    const updatedNote: Note = {
      id: 0,
      content: newNotes,
      author_id: 0,
      author_email: '',
      author_name: '',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    // Update React Query cache for this specific note
    queryClient.setQueryData([AttioQueryKeys.BULK_NOTES, recordId], updatedNote);

    // Update displayed valuations optimistically
    setDisplayedValuations(prevValuations => 
      prevValuations.map(valuation => 
        valuation.record_id === recordId 
          ? { 
            ...valuation, 
            note: updatedNote
          }
          : valuation
      )
    );

    // Invalidate cache to ensure data consistency
    queryClient.invalidateQueries({
      queryKey: [AttioQueryKeys.VALUATIONS_ALL, userRecordId, filters, orderBy, order]
    });

    // Invalidate additional data cache for this specific record
    queryClient.invalidateQueries({
      queryKey: [AttioQueryKeys.ADDITIONAL_DATA],
      predicate: query => {
        const {queryKey} = query;
        if (Array.isArray(queryKey) && queryKey[0] === AttioQueryKeys.ADDITIONAL_DATA) {
          const recordIds = queryKey[1]?.toString().split(',') || [];
          return recordIds.includes(recordId);
        }
        return false;
      }
    });
  }, [queryClient, userRecordId, filters, orderBy, order]);

  const updateValuationEngagementManager = useCallback(async (
    recordId: string, 
    engagementManagerOption: EngagementManagerOption
  ) => {
    const engagementManagerData = await loadEngagementManagerData(engagementManagerOption.targetRecordId);
    
    queryClient.setQueryData(
      [AttioQueryKeys.ENGAGEMENT_MANAGER, engagementManagerOption.targetRecordId],
      engagementManagerData.data
    );

    setEngagementManagersCache(prevCache => {
      const newCache = new Map(prevCache);
      newCache.set(engagementManagerOption.targetRecordId, engagementManagerData.data);
      return newCache;
    });

    setDisplayedValuations(prevValuations => 
      prevValuations.map(valuation => 
        valuation.record_id === recordId 
          ? { 
            ...valuation, 
            engagement_manager: engagementManagerOption.name,
            engagement_manager_image_src: engagementManagerOption.imageSrc,
            engagement_manager_id: engagementManagerOption.targetRecordId,
          }
          : valuation
      )
    );
  }, [queryClient, loadEngagementManagerData]);

  // Function to clear all caches (useful for manual refresh)
  const clearAllCaches = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [AttioQueryKeys.VALUATIONS_ALL]
    });
    queryClient.invalidateQueries({
      queryKey: [AttioQueryKeys.ADDITIONAL_DATA]
    });
    queryClient.invalidateQueries({
      queryKey: [AttioQueryKeys.BULK_NOTES]
    });
    queryClient.invalidateQueries({
      queryKey: [AttioQueryKeys.OTHER_CONSULTANT]
    });
    queryClient.invalidateQueries({
      queryKey: [AttioQueryKeys.ENGAGEMENT_MANAGER]
    });
    localStorage.removeItem(VALUATIONS_SORT_KEY);
    setDisplayedValuations([]);
    setAllValuations([]);
    setIsInitialLoadComplete(false);
    setLoadedBatches(new Set());
  }, [queryClient]);

  return {
    // Data
    rows: displayedValuations,
    totalCount,
    staffUsers,
    isLoadingStaffUsers,
    setSelectedUserEmail,
    selectedUserEmail,

    // Loading states
    isLoading: isLoadingUserRecord || isLoadingAllValuations || isLoadingBulkNotes || (!isInitialLoadComplete && allValuations.length > 0),
    isLoadingAdditionalData,
    isError: false,
    error: null,
    
    // Update functions
    updateValuation,
    updateValuationStatus,
    updateValuationNotes,
    updateValuationEngagementManager,
    clearAllCaches,
  };
};
